/**
 * Deterministic gradient noise used for all terrain generation.
 * Analytic derivatives are returned alongside the value so terrain normals and
 * slope-aware erosion are exact rather than finite-differenced.
 *
 * Everything in here returns [value, d/dx, d/dy] and the derivative is the *exact*
 * derivative of the value it is returned with. That matters more than it sounds:
 * physics stands the bike on the normal, the mesh is displaced by the value, and if
 * the two disagree the bike sits at an angle the ground does not have.
 */

import { hash2 } from '../core/math';

const PERM_SIZE = 512;
const MASK = PERM_SIZE - 1;

export class Noise2D {
  private readonly gradX = new Float32Array(PERM_SIZE);
  private readonly gradY = new Float32Array(PERM_SIZE);
  private readonly perm = new Uint16Array(PERM_SIZE * 2);

  constructor(seed: number) {
    for (let i = 0; i < PERM_SIZE; i++) {
      const a = hash2(i, seed, 0x1234) * Math.PI * 2;
      this.gradX[i] = Math.cos(a);
      this.gradY[i] = Math.sin(a);
      this.perm[i] = i;
    }
    // Deterministic Fisher-Yates using the same hash stream.
    for (let i = PERM_SIZE - 1; i > 0; i--) {
      const j = Math.floor(hash2(i, seed, 0x5678) * (i + 1));
      const t = this.perm[i];
      this.perm[i] = this.perm[j];
      this.perm[j] = t;
    }
    for (let i = 0; i < PERM_SIZE; i++) this.perm[i + PERM_SIZE] = this.perm[i];
  }

  /** Returns [value, dValue/dx, dValue/dy]; value in roughly [-1,1]. */
  sample(x: number, y: number, out: [number, number, number]): [number, number, number] {
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    const xf = x - xi;
    const yf = y - yi;

    // Quintic fade and its derivative.
    const u = xf * xf * xf * (xf * (xf * 6 - 15) + 10);
    const v = yf * yf * yf * (yf * (yf * 6 - 15) + 10);
    const du = 30 * xf * xf * (xf * (xf - 2) + 1);
    const dv = 30 * yf * yf * (yf * (yf - 2) + 1);

    // The table is duplicated at PERM_SIZE, so the coordinate mask MUST be
    // PERM_SIZE-1 too. Masking with 255 against a 512-long table means cell 255's
    // right-hand corner reads perm[256+p] while cell 256's left-hand corner reads
    // perm[0+p] — different gradients for the same lattice point, i.e. a hard step
    // in the height field along the whole line. Index -1 masks to 255, so with the
    // old mask there was a cliff through world x=0 and z=0 at every octave at once.
    const X = xi & MASK;
    const Y = yi & MASK;
    const a = this.perm[X + this.perm[Y]] & MASK;
    const b = this.perm[X + 1 + this.perm[Y]] & MASK;
    const c = this.perm[X + this.perm[Y + 1]] & MASK;
    const d = this.perm[X + 1 + this.perm[Y + 1]] & MASK;

    const gax = this.gradX[a], gay = this.gradY[a];
    const gbx = this.gradX[b], gby = this.gradY[b];
    const gcx = this.gradX[c], gcy = this.gradY[c];
    const gdx = this.gradX[d], gdy = this.gradY[d];

    const n00 = gax * xf + gay * yf;
    const n10 = gbx * (xf - 1) + gby * yf;
    const n01 = gcx * xf + gcy * (yf - 1);
    const n11 = gdx * (xf - 1) + gdy * (yf - 1);

    const k0 = n00;
    const k1 = n10 - n00;
    const k2 = n01 - n00;
    const k3 = n00 - n10 - n01 + n11;

    // The corner values are themselves linear in (xf,yf) — a derivative that only
    // differentiates the fade weights is wrong by several degrees of surface normal
    // almost everywhere, which is enough to make the bike stand at an angle the
    // ground does not have. Differentiate both the weights and the corners.
    const a0 = gax;
    const a1 = gbx - gax;
    const a2 = gcx - gax;
    const a3 = gax - gbx - gcx + gdx;
    const b0 = gay;
    const b1 = gby - gay;
    const b2 = gcy - gay;
    const b3 = gay - gby - gcy + gdy;

    out[0] = k0 + k1 * u + k2 * v + k3 * u * v;
    out[1] = a0 + a1 * u + a2 * v + a3 * u * v + du * (k1 + k3 * v);
    out[2] = b0 + b1 * u + b2 * v + b3 * u * v + dv * (k2 + k3 * u);
    return out;
  }
}

const tmp: [number, number, number] = [0, 0, 0];

/** Fractal Brownian motion with analytic derivatives. Output in roughly [-1,1]. */
export function fbm(
  noise: Noise2D,
  x: number,
  y: number,
  octaves: number,
  lacunarity: number,
  gain: number,
  out: [number, number, number],
): [number, number, number] {
  let amp = 1;
  let freq = 1;
  let sum = 0;
  let dx = 0;
  let dy = 0;
  let norm = 0;
  for (let i = 0; i < octaves; i++) {
    noise.sample(x * freq, y * freq, tmp);
    sum += tmp[0] * amp;
    dx += tmp[1] * amp * freq;
    dy += tmp[2] * amp * freq;
    norm += amp;
    amp *= gain;
    freq *= lacunarity;
  }
  out[0] = sum / norm;
  out[1] = dx / norm;
  out[2] = dy / norm;
  return out;
}

/**
 * Ridged multifractal — the shape that reads as an actual massif rather than dunes.
 *
 * Two things make it landform instead of noise:
 *
 *  1. Each octave is `(1 - |n|)^2`. The square is smooth at its zero and creased at
 *     its maximum, so crests come out as knife edges and troughs come out rounded.
 *     Plain `1-|n|` gives you creased *valleys* too, which is the crunchy-gully look.
 *
 *     That crest crease is a genuine slope discontinuity, and on the *fine* octaves it
 *     is what fires a bike off the mountain — see `crestRound` below and the R4 section
 *     of terrain.ts. It is wanted on the coarse octaves (that is an arete) and it is a
 *     defect on the fine ones (that is a kerb), so it is now a parameter rather than a
 *     property of the function.
 *
 *  2. Every octave is multiplied by a weight taken from the previous octave's signal
 *     (`erosion`). Where the previous octave was near a crest the weight is 1 and the
 *     detail lands; down in a trough the weight collapses to 0 and the valley floor
 *     stays smooth and wide. This is the erosion term, and it is carried through the
 *     derivative exactly — no "treat the damping as locally constant" fudge, because
 *     that produces normals which do not match the surface they came from.
 *
 * Output is in [0,1]: 1 at a crest, 0 on a valley floor.
 *
 * `crestRound` rounds the crease at each octave's crest by replacing |n| with
 * sqrt(n^2 + e^2) - e. Subtracting e keeps the crest value at exactly 1, so the field's
 * range and therefore any datum calibrated against it are unchanged. The replacement is
 * C-infinity, so it removes a discontinuity without introducing one, and its derivative
 * below is exact (n/sqrt(n^2+e^2)), not an approximation.
 *
 * The rounding half-width is scaled by each octave's own frequency, which is the whole
 * point: octave i is sampled at `freq = lacunarity^i`, so `e = crestRound * freq` makes
 * the rounded band the SAME WIDTH IN WORLD UNITS at every scale. A 700 m arete keeps its
 * knife edge (it is rounded over a couple of metres out of 700); a 20 m ridgelet at the
 * bottom of the octave stack, which a 371 mm wheel would otherwise hit as a kerb, is
 * rounded over the same couple of metres and becomes something you can roll. One
 * constant, one sentence: no ridge crest anywhere on the mountain is sharper than a bike
 * can ride, and none is rounder than it needs to be.
 *
 * Pass 0 to get the exact previous behaviour.
 */
export function ridgedMF(
  noise: Noise2D,
  x: number,
  y: number,
  octaves: number,
  lacunarity: number,
  gain: number,
  erosion: number,
  out: [number, number, number],
  crestRound = 0,
): [number, number, number] {
  let amp = 1;
  let freq = 1;
  let sum = 0;
  let dx = 0;
  let dy = 0;
  let norm = 0;
  // Multifractal weight carried between octaves, with its exact gradient.
  let w = 1;
  let wdx = 0;
  let wdy = 0;

  for (let i = 0; i < octaves; i++) {
    noise.sample(x * freq, y * freq, tmp);
    const n = tmp[0];
    // |n|, or its rounded stand-in, together with d|n|/dn. The branch is on a constant
    // for the whole loop, so it predicts perfectly; measured, the rounded path costs
    // 2% of a full terrain sample.
    let an: number;
    let sgn: number;
    if (crestRound > 0) {
      const e = crestRound * freq;
      const rt = Math.sqrt(n * n + e * e);
      an = rt - e;
      sgn = n / rt;
    } else {
      an = n < 0 ? -n : n;
      sgn = n >= 0 ? 1 : -1;
    }
    const s = 1 - an;
    const sq = s * s;
    // d(sq)/dx = 2*s*d(s)/dx, d(s)/dx = -sgn * dn/dx, dn/dx = tmp[1]*freq
    const k = -2 * s * sgn * freq;
    const sqx = k * tmp[1];
    const sqy = k * tmp[2];

    const v = sq * w;
    const vdx = sqx * w + sq * wdx;
    const vdy = sqy * w + sq * wdy;

    sum += v * amp;
    dx += vdx * amp;
    dy += vdy * amp;
    norm += amp;

    const t = v * erosion;
    if (t < 1) {
      w = t;
      wdx = vdx * erosion;
      wdy = vdy * erosion;
    } else {
      w = 1;
      wdx = 0;
      wdy = 0;
    }

    amp *= gain;
    freq *= lacunarity;
  }

  const inv = 1 / norm;
  out[0] = sum * inv;
  out[1] = dx * inv;
  out[2] = dy * inv;
  return out;
}

/** Legacy signed-range wrapper: output in [-1,1]. Kept so callers outside this
 *  module do not have to care which form they want. */
export function ridged(
  noise: Noise2D,
  x: number,
  y: number,
  octaves: number,
  lacunarity: number,
  gain: number,
  out: [number, number, number],
): [number, number, number] {
  ridgedMF(noise, x, y, octaves, lacunarity, gain, 1.35, out);
  out[0] = out[0] * 2 - 1;
  out[1] *= 2;
  out[2] *= 2;
  return out;
}
